// src/pages/offlineSales/OfflineSalesOrderDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Printer, XCircle, CreditCard, RotateCcw,
  ChevronDown, ChevronUp, User, Star, Share2, Package, Wallet, Truck, FileText, History,
  Calendar, Ban, Activity, Edit, RefreshCw, Box, Clock, CheckCircle, AlertCircle, Loader2,
  Mail, MapPin, Store, ExternalLink,
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getOrder, voidOrder, recordOrderPayment, getOfflineOrderAuditHistory, getOfflineOrderAuditSummary,
  getSalesReps, assignSalesRep,
} from '../../services/api/offlineSales/orders';
import { getOrderCourierTracking } from '../../services/api/orders';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import { PaymentMethodIcon } from '../../utils/paymentMethodIcons';
import { getCssVar } from '../../utils/themeColors';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import OrderReceiptPrintModal from './components/OrderReceiptPrintModal';
import ZReportModal from './components/ZReportModal';
import { toSalesRepOptions } from './salesRepOptions';
import styles from './OfflineSalesOrderDetailPage.module.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PAYMENT_COLORS = { paid: 'success', partial: 'warning', unpaid: 'error' };

const Badge = ({ type, label, color }) => (
  color
    ? <span className={styles.badge} style={{ background: `${color}22`, color }}>{label}</span>
    : <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const getOfflineStatusIcon = (statusKey) => {
  const icons = {
    offline_completed: <CheckCircle size={16} />,
    offline_credit: <Clock size={16} />,
    offline_on_hold: <Clock size={16} />,
    offline_voided: <XCircle size={16} />,
    offline_returned: <XCircle size={16} />,
    offline_partially_returned: <RotateCcw size={16} />,
  };
  return icons[statusKey] || <Clock size={16} />;
};
const AUDIT_DOC_ROUTES = {
  quotation: (id) => `/offline-sales/quotations/${id}`,
  delivery_note: (id) => `/offline-sales/delivery-notes/${id}`,
  return_note: (id) => `/offline-sales/return-notes/${id}`,
  credit_note: (id) => `/offline-sales/credit-notes/${id}`,
};
const AUDIT_MONEY_FIELDS = ['unitPrice', 'lineDiscount', 'lineTotal', 'totalRefundAmount', 'amount', 'appliedAmount'];
const AUDIT_ITEM_FIELDS = ['quantity', 'freeQuantity', 'unitPrice', 'lineDiscount', 'lineTotal'];
const AUDIT_ITEM_GROUPS = [
  { key: 'updated', labelKey: 'offlineSales.auditItemUpdated' },
  { key: 'added', labelKey: 'offlineSales.auditItemAdded' },
  { key: 'removed', labelKey: 'offlineSales.auditItemRemoved' },
];
const AUDIT_EVENT_TONES = { CREATED: 'success', CONVERTED: 'success', ISSUED: 'success', VOIDED: 'error', CANCELLED: 'error', APPLIED: 'warning', UPDATED: 'info' };
const AUDIT_ACTION_TONES = { CREATE: 'success', DELETE: 'error', STATUS_CHANGE: 'warning', UPDATE: 'info' };
const AUDIT_TONE_VARS = { success: '--color-success-dark', error: '--color-error', warning: '--color-warning', info: '--color-info' };
const AUDIT_TONE_ICONS = { success: CheckCircle, error: XCircle, warning: RefreshCw, info: Edit };
const getAuditTone = (audit) => {
  const doc = audit.changes?.offlineDocument;
  return (doc ? AUDIT_EVENT_TONES[doc.event] : AUDIT_ACTION_TONES[audit.action]) || 'neutral';
};
const getAuditActionIcon = (audit) => {
  const Icon = AUDIT_TONE_ICONS[getAuditTone(audit)] || Activity;
  return <Icon size={14} />;
};
const getAuditActionColor = (audit) => getCssVar(AUDIT_TONE_VARS[getAuditTone(audit)] || '--color-text-secondary');
const camelCaseToTitle = (str) => str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
const formatChangeValue = (value) => {
  if (value === null || value === undefined) return 'Empty';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// ─── Void Reason Modal ────────────────────────────────────────────────────────
const VoidModal = ({ onConfirm, onClose, loading }) => {
  const { t } = useLocalization();
  const [reason, setReason] = useState('');
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.smallModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('offlineSales.voidOrderTitle')}</h3>
          <button onClick={onClose} className={styles.iconBtn}><XCircle size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.warnText}>{t('offlineSales.voidWarning')}</p>
          <label>{t('offlineSales.voidReason')} *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            className={styles.textarea}
            placeholder={t('offlineSales.voidReasonPlaceholder')}
            autoFocus
          />
        </div>
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.cancel', 'Cancel')}</button>
          <button
            disabled={!reason.trim() || loading}
            onClick={() => onConfirm(reason)}
            className={styles.dangerBtn}
          >
            {loading ? '...' : t('offlineSales.confirmVoid')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Assign Sales Rep Modal ───────────────────────────────────────────────────
const AssignSalesRepModal = ({ current, onConfirm, onClose, loading }) => {
  const { t } = useLocalization();
  const [reps, setReps] = useState([]);
  const [salesRepUserId, setSalesRepUserId] = useState(current?.id || null);

  useEffect(() => {
    getSalesReps()
      .then(res => {
        const list = res?.salesReps || [];
        if (current && !list.some(r => r.id === current.id)) list.push(current);
        setReps(list);
      })
      .catch(() => setReps(current ? [current] : []));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.smallModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('offlineSales.assignSalesRep')}</h3>
          <button onClick={onClose} className={styles.iconBtn}><XCircle size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          <label>{t('offlineSales.salesRep')}</label>
          <Dropdown
            value={salesRepUserId || ''}
            onChange={value => setSalesRepUserId(value ? parseInt(value) : null)}
            options={toSalesRepOptions(reps)}
            placeholder={t('offlineSales.selectSalesRep')}
            searchable
          />
        </div>
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.cancel', 'Cancel')}</button>
          <button
            disabled={loading || (salesRepUserId || null) === (current?.id || null)}
            onClick={() => onConfirm(salesRepUserId)}
            className={styles.primaryBtn}
          >
            {loading ? '...' : t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Record Payment Modal ─────────────────────────────────────────────────────
const RecordPaymentModal = ({ balance, onConfirm, onClose, loading }) => {
  const { t, formatCurrency } = useLocalization();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.smallModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('offlineSales.recordCreditPayment')}</h3>
          <button onClick={onClose} className={styles.iconBtn}><XCircle size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          <p>{t('offlineSales.outstandingBalance')}: <strong>{formatCurrency(balance)}</strong></p>
          <label>{t('offlineSales.paymentMethod')}</label>
          <Dropdown
            value={method}
            onChange={value => setMethod(value || 'cash')}
            options={[
              { value: 'cash', label: t('offlineSales.cash') },
              { value: 'card', label: t('offlineSales.card') },
              { value: 'bank_transfer', label: t('offlineSales.bankTransfer') },
            ]}
            allowClear={false}
          />
          <label style={{ marginTop: '0.75rem', display: 'block' }}>{t('offlineSales.amountToPayNow')}</label>
          <input
            type="number"
            min="0.01"
            max={balance}
            step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className={styles.input}
            autoFocus
          />
        </div>
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.cancel', 'Cancel')}</button>
          <button
            disabled={!amount || parseFloat(amount) <= 0 || loading}
            onClick={() => onConfirm([{ paymentMethodKey: method, amount: parseFloat(amount) }])}
            className={styles.primaryBtn}
          >
            {loading ? '...' : t('offlineSales.confirmPayment')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OfflineSalesOrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { success, error } = useAlert();
  const { hasPermission } = usePermissions();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showVoid, setShowVoid] = useState(false);
  const [voidLoading, setVoidLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showAssignRep, setShowAssignRep] = useState(false);
  const [assignRepLoading, setAssignRepLoading] = useState(false);
  const [showZReport, setShowZReport] = useState(false);
  const [courierTracking, setCourierTracking] = useState(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [showCourierTracking, setShowCourierTracking] = useState(false);

  // Audit history — same shape/behavior as src/pages/orders/OrderDetailsPage.jsx
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLimit] = useState(10);
  const [auditTotal, setAuditTotal] = useState(0);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const result = await getOrder(orderId);
      setOrder(result.order || result);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!orderId) return;
    getOfflineOrderAuditSummary(orderId).then((response) => setAuditSummary(response.summary)).catch(() => {});
  }, [orderId]);

  const loadAuditHistory = async (page = 1) => {
    try {
      setIsLoadingAudit(true);
      setAuditError(null);
      const response = await getOfflineOrderAuditHistory(orderId, { page, limit: auditLimit });
      setAuditHistory(response.audits);
      setAuditTotal(response.total);
      setAuditPage(page);
    } catch (err) {
      setAuditError(t('ecomOrders.failedToLoadAuditHistory'));
    } finally {
      setIsLoadingAudit(false);
    }
  };

  useEffect(() => {
    if (showAuditHistory && !auditHistory.length) loadAuditHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAuditHistory]);

  useEffect(() => {
    if (showCourierTracking && !courierTracking && order?.airwaybillNumber) loadCourierTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCourierTracking, order]);

  const loadCourierTracking = async () => {
    try {
      setIsLoadingTracking(true);
      setTrackingError(null);
      const response = await getOrderCourierTracking(orderId);
      setCourierTracking(response.data);
    } catch (err) {
      setTrackingError(err.response?.data?.message || t('ecomOrders.failedToLoadCourierTracking'));
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const renderCreateChanges = (changes) => {
    if (!changes.created || !changes.created.to) return null;
    return (
      <div className={styles.auditChanges}>
        {Object.entries(changes.created.to).map(([field, value]) => (
          <div key={field} className={styles.auditChange}>
            <span className={styles.changeField}>{camelCaseToTitle(field)}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>Empty</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatChangeValue(value)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUpdateChanges = (changes) => (
    <div className={styles.auditChanges}>
      {Object.entries(changes).map(([field, change]) => (
        <div key={field} className={styles.auditChange}>
          <span className={styles.changeField}>{camelCaseToTitle(field)}</span>
          <div className={styles.changeValues}>
            <span className={styles.oldValue}>{formatChangeValue(change.from)}</span>
            <span className={styles.changeArrow}>→</span>
            <span className={styles.newValue}>{formatChangeValue(change.to)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const PAYMENT_RECORD_AUDIT_FIELDS = [
    { key: 'paymentMethodLabel', label: 'Payment Method' },
    { key: 'paymentAmount', label: 'Amount' },
    { key: 'reference', label: 'Reference' },
    { key: 'paymentDate', label: 'Payment Date' },
    { key: 'isVerified', label: 'Verified' },
  ];
  const renderPaymentRecordChange = (paymentRecordChange) => {
    const { from, to } = paymentRecordChange;
    const rows = PAYMENT_RECORD_AUDIT_FIELDS
      .map(({ key, label }) => ({ label, from: from?.[key], to: to?.[key] }))
      .filter(({ from: f, to: tt }) => (f ?? null) !== (tt ?? null));
    if (rows.length === 0) return null;
    return (
      <div className={styles.auditChanges}>
        {rows.map(({ label, from: f, to: tt }) => (
          <div key={label} className={styles.auditChange}>
            <span className={styles.changeField}>{label}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatChangeValue(f)}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatChangeValue(tt)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const formatAuditValue = (field, value) => {
    if (value === null || value === undefined || value === '') return t('offlineSales.auditEmpty');
    if (AUDIT_MONEY_FIELDS.includes(field)) return formatCurrency(value);
    if (field === 'status') return t(`offlineSales.status_${value}`, String(value));
    if (field === 'refundMethod') return t(`offlineSales.refundMethod_${value}`, String(value));
    return formatChangeValue(value);
  };

  const renderChangeRows = (rows) => {
    if (rows.length === 0) return null;
    return (
      <div className={styles.auditChanges}>
        {rows.map(({ field, from, to }) => (
          <div key={field} className={styles.auditChange}>
            <span className={styles.changeField}>{t(`offlineSales.auditField_${field}`, camelCaseToTitle(field))}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatAuditValue(field, from)}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatAuditValue(field, to)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // `items` is either `{ added, removed, updated }` (edits) or a flat list (document lines)
  const renderItemChanges = (items) => {
    if (!items) return null;
    const groups = Array.isArray(items) ? { added: items } : items;
    return AUDIT_ITEM_GROUPS.flatMap(({ key, labelKey }) => (groups[key] || []).map((item, index) => {
      const rows = key === 'updated'
        ? Object.entries(item.changes || {}).map(([field, value]) => ({ field, from: value.from ?? null, to: value.to ?? null }))
        : AUDIT_ITEM_FIELDS
          .filter((field) => item[field] !== null && item[field] !== undefined && !(field === 'freeQuantity' && !item[field]))
          .map((field) => (key === 'added' ? { field, from: null, to: item[field] } : { field, from: item[field], to: null }));
      return (
        <div key={`${key}-${index}`} className={styles.auditItemChange}>
          <div className={styles.auditItemHeader}>
            <strong>{item.productName || t('common.na', 'N/A')}</strong>
            {item.sku && <span className={styles.auditItemSku}>{item.sku}</span>}
            {!Array.isArray(items) && <span className={styles.auditPaymentRecordTag}>{t(labelKey)}</span>}
          </div>
          {renderChangeRows(rows)}
        </div>
      );
    }));
  };

  const renderOfflineDocumentChange = (doc) => {
    const fields = [...new Set([...Object.keys(doc.from || {}), ...Object.keys(doc.to || {})])];
    const rows = fields
      .map((field) => ({ field, from: doc.from?.[field] ?? null, to: doc.to?.[field] ?? null }))
      .filter((row) => row.from !== row.to);
    if (doc.orderStatusChange) rows.unshift({ field: 'orderStatus', ...doc.orderStatusChange });
    return (
      <>
        {renderChangeRows(rows)}
        {renderItemChanges(doc.items)}
      </>
    );
  };

  const getAuditLabel = (audit) => {
    const doc = audit.changes?.offlineDocument;
    if (doc) return t(`offlineSales.audit_${doc.docType}_${doc.event}`, doc.event);
    const payment = audit.changes?.paymentRecord;
    if (payment) {
      const actionLabel = t(`offlineSales.audit_payment_${audit.action}`, audit.action);
      return payment.event ? t(`offlineSales.audit_payment_${payment.event}`, actionLabel) : actionLabel;
    }
    return t(`offlineSales.audit_${audit.action}`, audit.action);
  };

  const renderAuditTag = (audit) => {
    const doc = audit.changes?.offlineDocument;
    if (audit.changes?.paymentRecord) {
      return <span className={styles.auditPaymentRecordTag}>{t('offlineSales.auditTag_payment')}</span>;
    }
    if (!doc) return null;
    const label = `${t(`offlineSales.auditTag_${doc.docType}`)}${doc.docNumber ? ` · ${doc.docNumber}` : ''}`;
    const routeFor = AUDIT_DOC_ROUTES[doc.docType];
    return routeFor && doc.docId
      ? <Link to={routeFor(doc.docId)} className={styles.auditPaymentRecordTag}>{label}</Link>
      : <span className={styles.auditPaymentRecordTag}>{label}</span>;
  };

  const handleVoid = async (reason) => {
    try {
      setVoidLoading(true);
      await voidOrder(orderId, reason);
      success('Order voided');
      setShowVoid(false);
      loadOrder();
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to void order');
    } finally {
      setVoidLoading(false);
    }
  };

  const handleAssignSalesRep = async (salesRepUserId) => {
    try {
      setAssignRepLoading(true);
      await assignSalesRep(orderId, salesRepUserId);
      success(t('offlineSales.salesRepAssigned'));
      setShowAssignRep(false);
      loadOrder();
    } catch (err) {
      error(err?.response?.data?.message || t('offlineSales.salesRepAssignFailed'));
    } finally {
      setAssignRepLoading(false);
    }
  };

  const handleRecordPayment = async (paymentDetails) => {
    try {
      setPaymentLoading(true);
      await recordOrderPayment(orderId, paymentDetails);
      success('Payment recorded');
      setShowPayment(false);
      loadOrder();
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePrint = () => setShowReceipt(true);

  const handleProcessReturn = () => {
    navigate(`/offline-sales/return-notes/create?orderNumber=${order.orderNumber}`);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!order) {
    return <div className={styles.notFound}>{t('errors.notFound', 'Order not found')}</div>;
  }

  const statusKey = order.OrderStatus?.key;
  const canVoid = statusKey !== 'offline_voided' && statusKey !== 'offline_returned' && hasPermission('void_offline_sales_order');
  const canReturn = (statusKey === 'offline_completed' || statusKey === 'offline_partially_returned') && hasPermission('create_return_note');
  const canRecordPayment = (order.paymentStatus === 'partial' || order.paymentStatus === 'unpaid') && statusKey !== 'offline_voided';
  const canEdit = !order.OrderStatus?.isClosed && hasPermission('edit_offline_sales_order');

  const packingBatch = order.batchOrderDetails?.PackingBatch;
  const hasRelatedDocuments = (order.deliveryNoteDocuments || []).length > 0
    || (order.creditNotes || []).length > 0
    || (order.returns || []).length > 0
    || !!packingBatch
    || !!order.sourceQuotation;
  const hasCourierInfo = !!(order.airwaybillNumber || order.assignedCourierAccount || order.courierStatus);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Package className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('offlineSales.orderDetails', 'Order Details')}</h1>
              <p className={styles.orderNumber}>#{order.orderNumber}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.secondaryButton} onClick={() => navigate('/offline-sales/orders')}>
              <ArrowLeft size={18} /> {t('offlineSales.backToOrders')}
            </button>
            <button className={styles.secondaryButton} onClick={handlePrint}>
              <Printer size={18} /> {t('offlineSales.printReceipt')}
            </button>
            {canEdit && (
              <button className={styles.primaryButton} onClick={() => navigate(`/offline-sales/orders/${order.id}/edit`)}>
                <Edit size={18} /> {t('common.edit')}
              </button>
            )}
            {order?.shareToken && (
              <button
                className={styles.secondaryButton}
                onClick={() => {
                  const url = `${window.location.origin}/invoice/${order.shareToken}`;
                  navigator.clipboard.writeText(url).then(() => success('Invoice link copied to clipboard!')).catch(() => error('Could not copy link'));
                }}
                title="Copy shareable invoice link"
              >
                <Share2 size={18} /> Share Invoice
              </button>
            )}
            {canVoid && (
              <button className={`${styles.secondaryButton} ${styles.reverseButton}`} onClick={() => setShowVoid(true)}>
                <XCircle size={18} /> {t('offlineSales.voidOrder')}
              </button>
            )}
            {canRecordPayment && (
              <button className={styles.secondaryButton} onClick={() => setShowPayment(true)}>
                <CreditCard size={18} /> {t('offlineSales.recordPayment')}
              </button>
            )}
            {canReturn && (
              <button className={styles.secondaryButton} onClick={handleProcessReturn}>
                <RotateCcw size={18} /> {t('offlineSales.processReturn')}
              </button>
            )}
            {hasPermission('create_delivery_note') && (
              <button className={styles.secondaryButton} onClick={() => navigate(`/offline-sales/delivery-notes/create?orderNumber=${order.orderNumber}`)}>
                <Truck size={18} /> {t('offlineSales.createDeliveryNote', 'Create Delivery Note')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        <div className={styles.mainColumn}>
          {/* ── Order Status ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <Activity size={20} />
                <h2>{t('offlineSales.orderStatus', 'Order Status')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem}>
                  <label>{t('offlineSales.statusCol')}</label>
                  <div
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: order.OrderStatus?.colorCode ? `${order.OrderStatus.colorCode}20` : '#F59E0B20',
                      borderColor: order.OrderStatus?.colorCode || '#F59E0B',
                      color: order.OrderStatus?.colorCode || '#92400e',
                    }}
                  >
                    {getOfflineStatusIcon(statusKey)}
                    <span>{t(`offlineSales.status_${statusKey}`, order.OrderStatus?.label)}</span>
                  </div>
                </div>
                <div className={styles.statusItem}>
                  <label>{t('offlineSales.paymentStatusCol')}</label>
                  <div className={`${styles.paymentTypeBadge} ${styles[`paymentTypeBadge_${PAYMENT_COLORS[order.paymentStatus] || 'default'}`] || ''}`}>
                    <CreditCard size={14} />
                    {t(`offlineSales.payment_${order.paymentStatus}`, order.paymentStatus)}
                  </div>
                </div>
                <div className={styles.statusItem}>
                  <label>{t('offlineSales.date')}</label>
                  <p>{formatDateTime(order.createdAt)}</p>
                </div>
                <div className={styles.statusItem}>
                  <label>{t(order.channel === 'pos' ? 'offlineSales.cashier' : 'ecomOrders.createdBy')}</label>
                  <p>{order.cashier?.name || '—'}</p>
                </div>
                {order.channel === 'offline' && (
                  <div className={styles.statusItem}>
                    <label>{t('offlineSales.salesRep')}</label>
                    <p>
                      {order.salesRep?.name || '—'}
                      {hasPermission('assign_offline_sales_rep') && (
                        <button
                          className={styles.sessionLink}
                          onClick={() => setShowAssignRep(true)}
                          title={t('offlineSales.assignSalesRep')}
                        >
                          <Edit size={13} />
                        </button>
                      )}
                    </p>
                  </div>
                )}
                {order.cashierSession && (
                  <div className={styles.statusItem}>
                    <label>{t('offlineSales.session')}</label>
                    <p>
                      <button className={styles.sessionLink} onClick={() => setShowZReport(true)}>
                        #{order.cashierSession.id}
                      </button>
                    </p>
                  </div>
                )}
                {statusKey === 'offline_voided' && (
                  <>
                    <div className={styles.statusItem}>
                      <label>{t('offlineSales.voidReason')}</label>
                      <p>{order.voidReason || '—'}</p>
                    </div>
                    <div className={styles.statusItem}>
                      <label>{t('offlineSales.date')} ({t('offlineSales.voidOrder')})</label>
                      <p>{order.voidedAt ? formatDateTime(order.voidedAt) : '—'}</p>
                    </div>
                    <div className={styles.statusItem}>
                      <label>{t('common.by', 'By')}</label>
                      <p>{order.voidedBy?.name || '—'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Customer Card ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <User size={20} />
                <h2>{t('offlineSales.customer')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              {order.customer ? (
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}><label>{t('offlineSales.customerName')}</label><p>{order.customer.name}</p></div>
                  {order.customer.phone && <div className={styles.infoItem}><label>{t('offlineSales.customerPhone')}</label><p>{order.customer.phone}</p></div>}
                  {order.customer.email && (
                    <div className={styles.infoItem}>
                      <label>{t('offlineSales.customerEmail', 'Email')}</label>
                      <a href={`mailto:${order.customer.email}`} className={styles.emailLink}>
                        <Mail size={14} />
                        {order.customer.email}
                      </a>
                    </div>
                  )}
                  {order.address && (
                    <div className={styles.infoItem}>
                      <label>{t('offlineSales.deliveryAddress', 'Delivery Address')}</label>
                      <p className={styles.addressText}>
                        <MapPin size={14} />
                        {order.address}
                      </p>
                    </div>
                  )}
                  {order.City?.name && (
                    <div className={styles.infoItem}>
                      <label>{t('offlineSales.deliveryCity', 'Delivery City')}</label>
                      <p>{order.City.name}</p>
                    </div>
                  )}
                  <div className={styles.infoItem}>
                    <label><Star size={13} /> {t('offlineSales.loyaltyPointsLabel')}</label>
                    <p>{order.customer.loyaltyPoints || 0} pts</p>
                  </div>
                  {parseFloat(order.loyaltyPointsRedeemed) > 0 && (
                    <div className={styles.infoItem}>
                      <label>{t('offlineSales.pointsToRedeem')}</label>
                      <p>{order.loyaltyPointsRedeemed} pts (−{formatCurrency(order.loyaltyDiscountAmount)})</p>
                    </div>
                  )}
                  {parseFloat(order.loyaltyPointsEarned) > 0 && (
                    <div className={styles.infoItem}>
                      <label>{t('offlineSales.loyaltyPointsLabel')} {t('common.earned', 'earned')}</label>
                      <p>+{order.loyaltyPointsEarned} pts</p>
                    </div>
                  )}
                  {order.customer.creditTermDays != null && (
                    <div className={styles.infoItem}><label>{t('offlineSales.creditTermDays')}</label><p>{order.customer.creditTermDays}</p></div>
                  )}
                  {order.customer.creditLimit != null && (
                    <div className={styles.infoItem}><label>{t('offlineSales.creditLimit')}</label><p>{formatCurrency(order.customer.creditLimit)}</p></div>
                  )}
                </div>
              ) : (
                <p className={styles.dim}>{t('offlineSales.cashSales')}</p>
              )}
            </div>
          </div>

          {/* ── Items ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <Package size={20} />
                <h2>{t('offlineSales.items')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.itemsTable}>
                <table>
                  <thead>
                    <tr>
                      <th>{t('offlineSales.items')}</th>
                      <th className={styles.textRight}>{t('offlineSales.unitPrice')}</th>
                      <th className={styles.textRight}>{t('offlineSales.qty')}</th>
                      <th className={styles.textRight}>{t('offlineSales.lineDiscount')}</th>
                      <th className={styles.textRight}>{t('offlineSales.lineTotal')}</th>
                      <th className={styles.textRight}>{t('offlineSales.returnQty')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.orderItems || []).map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className={styles.productInfo}>
                            {getImageUrl(item.Product?.image) ? (
                              <img
                                src={getImageUrl(item.Product?.image)}
                                alt={item.Product?.name || ''}
                                className={styles.productThumb}
                                onError={handleImageError}
                              />
                            ) : (
                              <div className={styles.productThumbPlaceholder} />
                            )}
                            <div className={styles.productText}>
                              <div className={styles.productName}>{item.Product?.name}</div>
                              {item.ProductVariation?.sku && (
                                <div className={styles.productSku}>SKU: {item.ProductVariation.sku}</div>
                              )}
                              {item.freeQuantity > 0 && (
                                <div className={styles.freeNote}>{t('offlineSales.itemsIncludedFree', { count: item.freeQuantity })}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={styles.textRight}>{formatCurrency(item.unitPrice)}</td>
                        <td className={styles.textRight}>{item.quantity}</td>
                        <td className={styles.textRight}>{formatCurrency(item.lineDiscount || 0)}</td>
                        <td className={styles.textRight}>{formatCurrency(item.lineTotal)}</td>
                        <td className={styles.textRight}>
                          {parseFloat(item.returnedQuantity) > 0
                            ? <span className={styles.returned}>{item.returnedQuantity}</span>
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Related Documents (merged) ── */}
          {hasRelatedDocuments && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <FileText size={20} />
                  <h2>{t('ecomOrders.relatedDocuments')}</h2>
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.relationsGrid}>
                  {packingBatch && (
                    <div className={styles.relationsSection}>
                      <div className={styles.relationsSectionHeader}>
                        <Box size={18} />
                        <h3>{t('ecomOrders.packingBatches')}</h3>
                      </div>
                      <div className={styles.relationsList}>
                        <Link to={`/packing-batches/${packingBatch.id}`} className={styles.relationLink}>
                          <span>{packingBatch.batchNumber}</span>
                          <ExternalLink size={14} />
                        </Link>
                      </div>
                    </div>
                  )}

                  {order.sourceQuotation && (
                    <div className={styles.relationsSection}>
                      <div className={styles.relationsSectionHeader}>
                        <FileText size={18} />
                        <h3>{t('offlineSales.quotations', 'Quotations')}</h3>
                      </div>
                      <div className={styles.relationsList}>
                        <Link to={`/offline-sales/quotations/${order.sourceQuotation.id}`} className={styles.relationLink}>
                          <span>{order.sourceQuotation.quotationNumber}</span>
                          <ExternalLink size={14} />
                        </Link>
                      </div>
                    </div>
                  )}

                  {(order.deliveryNoteDocuments || []).length > 0 && (
                    <div className={styles.relationsSection}>
                      <div className={styles.relationsSectionHeader}>
                        <Truck size={18} />
                        <h3>{t('offlineSales.deliveryNotes', 'Delivery Notes')}</h3>
                      </div>
                      <div className={styles.relationsList}>
                        {order.deliveryNoteDocuments.map((dn) => (
                          <Link key={dn.id} to={`/offline-sales/delivery-notes/${dn.id}`} className={styles.relationLink}>
                            <span>{dn.deliveryNoteNumber}</span>
                            <span className={styles.relationMeta}>
                              <Badge type={dn.status === 'delivered' ? 'success' : 'default'} label={t(`offlineSales.status_${dn.status}`, dn.status || 'delivered')} />
                              <ExternalLink size={14} />
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {(order.creditNotes || []).length > 0 && (
                    <div className={styles.relationsSection}>
                      <div className={styles.relationsSectionHeader}>
                        <FileText size={18} />
                        <h3>{t('offlineSales.creditNotes', 'Credit Notes')}</h3>
                      </div>
                      <div className={styles.relationsList}>
                        {order.creditNotes.map((cn) => (
                          <Link key={cn.id} to={`/offline-sales/credit-notes/${cn.id}`} className={styles.relationLink}>
                            <span>{cn.creditNoteNumber} ({formatCurrency(cn.appliedAmount)} / {formatCurrency(cn.amount)})</span>
                            <span className={styles.relationMeta}>
                              <Badge type={cn.status === 'applied' ? 'success' : 'info'} label={t(`offlineSales.status_${cn.status}`, cn.status || 'active')} />
                              <ExternalLink size={14} />
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {(order.returns || []).length > 0 && (
                    <div className={styles.relationsSection}>
                      <div className={styles.relationsSectionHeader}>
                        <RotateCcw size={18} />
                        <h3>{t('offlineSales.returns', 'Return Notes')}</h3>
                      </div>
                      <div className={styles.relationsList}>
                        {order.returns.map((ret) => (
                          <Link key={ret.id} to={`/offline-sales/return-notes/${ret.id}`} className={styles.relationLink}>
                            <span>{ret.returnNumber} - {formatCurrency(ret.totalRefundAmount)}</span>
                            <span className={styles.relationMeta}>
                              <Badge type="warning" label={t(`offlineSales.refundMethod_${ret.refundMethod}`, ret.refundMethod || 'refund')} />
                              <ExternalLink size={14} />
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Courier Information ── */}
          {hasCourierInfo && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <Truck size={20} />
                  <h2>{t('ecomOrders.courierInformation')}</h2>
                </div>
                {order.airwaybillNumber && (
                  <button
                    onClick={() => setShowCourierTracking(!showCourierTracking)}
                    className={styles.toggleButton}
                  >
                    {showCourierTracking ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {t('ecomOrders.trackingDetails')}
                  </button>
                )}
              </div>
              <div className={styles.cardContent}>
                <div className={styles.infoGrid}>
                  {order.airwaybillNumber && (
                    <div className={styles.infoItem}><label>{t('ecomOrders.airwaybillNumber')}</label><p className={styles.airwaybillText}>{order.airwaybillNumber}</p></div>
                  )}
                  {order.assignedCourierAccount && (
                    <div className={styles.infoItem}><label>{t('ecomOrders.courierName')}</label><p>{order.assignedCourierAccount.accountName}</p></div>
                  )}
                  {order.courierStatus && (
                    <div className={styles.infoItem}><label>{t('ecomOrders.courierStatus')}</label><p>{order.courierStatus.statusLabel}</p></div>
                  )}
                  {order.dispatchedDate && (
                    <div className={styles.infoItem}><label>{t('ecomOrders.dispatchedDate')}</label><p>{formatDate(order.dispatchedDate)}</p></div>
                  )}
                </div>

                {showCourierTracking && (
                  <div className={styles.trackingSection}>
                    {isLoadingTracking ? (
                      <div className={styles.trackingLoading}>
                        <Loader2 className={styles.loadingSpinner} />
                        <span>{t('ecomOrders.loadingTracking')}</span>
                      </div>
                    ) : trackingError ? (
                      <div className={styles.trackingError}>
                        <AlertCircle size={16} />
                        <span>{trackingError}</span>
                      </div>
                    ) : courierTracking ? (
                      <>
                        <div className={styles.trackingSummary}>
                          <div className={styles.trackingItem}>
                            <label>{t('ecomOrders.codValue')}</label>
                            <p>{formatCurrency(courierTracking.codValue || 0)}</p>
                          </div>
                          <div className={styles.trackingItem}>
                            <label>{t('ecomOrders.totalCourierFees')}</label>
                            <p>{formatCurrency(courierTracking.totalCourierFees || 0)}</p>
                          </div>
                        </div>

                        {courierTracking.packageTimeline && courierTracking.packageTimeline.length > 0 && (
                          <div className={styles.timeline}>
                            <h4>{t('ecomOrders.packageTimeline')}</h4>
                            <div className={styles.timelineList}>
                              {courierTracking.packageTimeline.map((event, idx) => (
                                <div key={idx} className={styles.timelineEvent}>
                                  <div className={styles.timelineMarker}></div>
                                  <div className={styles.timelineContent}>
                                    <div className={styles.timelineDate}>
                                      {event.date ? event.date : 'N/A'}
                                    </div>
                                    <div className={styles.timelineNote}>{event.note}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Audit History ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <History size={20} />
                <h2>{t('ecomOrders.auditHistory')}</h2>
              </div>
              <button className={styles.accordionToggle} onClick={() => setShowAuditHistory(o => !o)}>
                {showAuditHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showAuditHistory ? t('common.hide') : t('common.show')}
              </button>
            </div>

            {auditSummary && (
              <div className={styles.auditSummaryBar}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('ecomOrders.totalChanges')}</span>
                  <span className={styles.summaryValue}>{auditSummary.totalChanges || 0}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('ecomOrders.lastModified')}</span>
                  <span className={styles.summaryValue}>{order.updatedAt ? formatDateTime(order.updatedAt) : 'N/A'}</span>
                </div>
              </div>
            )}

            {showAuditHistory && (
              <div className={styles.cardContent}>
                {isLoadingAudit ? (
                  <div className={styles.emptyState}><Loader2 size={24} /><p>{t('common.loading')}</p></div>
                ) : auditError ? (
                  <div className={styles.emptyState}><AlertCircle size={24} /><p>{auditError}</p></div>
                ) : auditHistory.length > 0 ? (
                  <>
                    <div className={styles.auditList}>
                      {auditHistory.map((audit) => (
                        <div key={audit.id} className={styles.auditItem}>
                          <div className={styles.auditHeader}>
                            <div className={styles.auditAction}>
                              <div className={styles.auditActionBadge} style={{ backgroundColor: `color-mix(in srgb, ${getAuditActionColor(audit)} 12%, transparent)`, color: getAuditActionColor(audit) }}>
                                {getAuditActionIcon(audit)}
                                <span>{getAuditLabel(audit)}</span>
                              </div>
                              {renderAuditTag(audit)}
                              <span className={styles.dim}>{t('offlineSales.auditBy', { name: audit.changedBy?.name || t('common.na', 'N/A') })}</span>
                            </div>
                            <div className={styles.dim}>
                              <Clock size={12} /> {formatDateTime(audit.createdAt)}
                            </div>
                          </div>
                          {audit.changes && Object.keys(audit.changes).length > 0 && (
                            audit.changes.paymentRecord
                              ? renderPaymentRecordChange(audit.changes.paymentRecord)
                              : audit.changes.offlineDocument
                              ? renderOfflineDocumentChange(audit.changes.offlineDocument)
                              : audit.action === 'CREATE' && audit.changes.created
                              ? renderCreateChanges(audit.changes)
                              : renderUpdateChanges(audit.changes)
                          )}
                          {audit.changeReason && (
                            <div className={styles.dim} style={{ marginTop: '0.5rem' }}>
                              <FileText size={12} /> {audit.changeReason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {auditTotal > auditLimit && (
                      <div className={styles.pagination}>
                        <button onClick={() => loadAuditHistory(auditPage - 1)} disabled={auditPage === 1} className={styles.paginationButton}>Previous</button>
                        <span className={styles.paginationInfo}>Page {auditPage} of {Math.ceil(auditTotal / auditLimit)}</span>
                        <button onClick={() => loadAuditHistory(auditPage + 1)} disabled={auditPage >= Math.ceil(auditTotal / auditLimit)} className={styles.paginationButton}>Next</button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <History size={32} />
                    <p>{t('ecomOrders.noAuditHistory')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles.sidebar}>
          {/* ── Order Summary ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <Wallet size={20} />
                <h2>{t('offlineSales.orderSummary')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.summaryGrid}>
                {parseFloat(order.totalDiscount) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.itemDiscount')}</span>
                    <span>−{formatCurrency(order.totalDiscount)}</span>
                  </div>
                )}
                {parseFloat(order.orderDiscount) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.orderDiscount')}</span>
                    <span>−{formatCurrency(order.orderDiscount)}</span>
                  </div>
                )}
                {parseFloat(order.loyaltyDiscountAmount) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.loyaltyDiscount')}</span>
                    <span>−{formatCurrency(order.loyaltyDiscountAmount)}</span>
                  </div>
                )}
                {parseFloat(order.deliveryCharge) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.deliveryCharge')}</span>
                    <span>+{formatCurrency(order.deliveryCharge)}</span>
                  </div>
                )}
                <div className={`${styles.summaryRow} ${styles.grandTotalRow}`}>
                  <span>{t('offlineSales.grandTotal')}</span>
                  <span>{formatCurrency(order.grandTotal)}</span>
                </div>
                <div className={styles.summaryDivider} />
                <div className={styles.summaryRow}>
                  <span>{t('offlineSales.totalPaid')}</span>
                  <span className={styles.paidAmount}>{formatCurrency(order.paidAmount)}</span>
                </div>
                {parseFloat(order.balanceAmount) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.balanceDue')}</span>
                    <span className={styles.codAmount}>{formatCurrency(order.balanceAmount)}</span>
                  </div>
                )}
                {parseFloat(order.paymentFees) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.paymentFeesCustomer')}</span>
                    <span>+{formatCurrency(order.paymentFees)}</span>
                  </div>
                )}
                {parseFloat(order.totalFees) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.totalPaymentFees')}</span>
                    <span>{formatCurrency(order.totalFees)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Payment Records ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <CreditCard size={20} />
                <h2>{t('offlineSales.payments')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              {(order.paymentRecords || []).length === 0
                ? <p className={styles.dim}>—</p>
                : (
                  <div className={styles.paymentList}>
                    {(order.paymentRecords || []).map((p) => (
                      <div key={p.id} className={`${styles.paymentRecord} ${p.status === 'void' ? styles.paymentRecordVoid : ''}`}>
                        <div className={styles.paymentHeader}>
                          <PaymentMethodIcon iconFileName={p.PaymentMethod?.iconFileName} label={p.PaymentMethod?.label} />
                          <span className={styles.paymentAmount}>{formatCurrency(p.paymentAmount)}</span>
                        </div>
                        {p.status === 'void' && (
                          <span className={styles.paymentVoidBadge} title={p.voidedAt ? formatDate(p.voidedAt) : undefined}>
                            <Ban size={12} />
                            {p.cheque?.status === 'bounced' ? t('offlineSales.chqStatus_bounced') : t('paymentVerification.void')}
                          </span>
                        )}
                        {p.reference && <div className={styles.paymentReference}>Ref: {p.reference}</div>}
                        <div className={styles.paymentDate}>
                          <Calendar size={12} />
                          {formatDate(p.paymentDate)}
                        </div>
                        {parseFloat(p.feeAmount) > 0 && (
                          <div className={styles.dim}>
                            {t('offlineSales.paymentFee')} {formatCurrency(p.feeAmount)} ({p.feeChargedToCustomer ? t('offlineSales.feeChargedToCustomer') : t('offlineSales.feeAbsorbed')})
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              }
            </div>
          </div>

          {/* ── Additional Information ── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FileText size={20} />
                <h2>{t('offlineSales.additionalInformation')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.infoList}>
                {order.Shop?.name && (
                  <div className={styles.infoListItem}><label>{t('ecomOrders.shopName')}</label><p><Store size={14} />{order.Shop.name}</p></div>
                )}
                <div className={styles.infoListItem}>
                  <label>{t('ecomOrders.createdBy')}</label>
                  <p><User size={14} />{order.cashier?.name || 'N/A'}</p>
                </div>
                <div className={styles.infoListItem}>
                  <label>{t('ecomOrders.createdAt')}</label>
                  <p><Calendar size={14} />{formatDateTime(order.createdAt)}</p>
                </div>
                {order.updatedAt && (
                  <div className={styles.infoListItem}><label>{t('ecomOrders.updatedAt')}</label><p><Calendar size={14} />{formatDateTime(order.updatedAt)}</p></div>
                )}
                {order.fulfillmentMethod && (
                  <div className={styles.infoListItem}>
                    <label>{t('offlineSales.fulfillmentMethod')}</label>
                    <p><Truck size={14} />{t(`offlineSales.${order.fulfillmentMethod === 'counter_pickup' ? 'counterPickup' : order.fulfillmentMethod === 'self_delivery' ? 'selfDelivery' : 'courier'}`)}</p>
                  </div>
                )}
                {order.fulfillmentMethod !== 'courier' && order.assignedWarehouse?.name && (
                  <div className={styles.infoListItem}><label>{t('offlineSales.warehouse')}</label><p><Store size={14} />{order.assignedWarehouse.name}</p></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showVoid && (
        <VoidModal
          onConfirm={handleVoid}
          onClose={() => setShowVoid(false)}
          loading={voidLoading}
        />
      )}
      {showPayment && (
        <RecordPaymentModal
          balance={parseFloat(order.balanceAmount)}
          onConfirm={handleRecordPayment}
          onClose={() => setShowPayment(false)}
          loading={paymentLoading}
        />
      )}
      {showAssignRep && (
        <AssignSalesRepModal
          current={order.salesRep || null}
          onConfirm={handleAssignSalesRep}
          onClose={() => setShowAssignRep(false)}
          loading={assignRepLoading}
        />
      )}
      {showReceipt && (
        <OrderReceiptPrintModal order={order} onClose={() => setShowReceipt(false)} />
      )}
      {showZReport && order.cashierSession && (
        <ZReportModal
          session={{ ...order.cashierSession, Shop: order.Shop }}
          onClose={() => setShowZReport(false)}
        />
      )}
    </div>
  );
};

export default OfflineSalesOrderDetailPage;
